import React, { useState, useEffect } from "react";
import { MessageCircle, Users, Mail, Heart } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { updatePrivacySettings } from "../../services/settingsService";
import { useReadingMode } from "../../hooks/useReadingMode";
import ToggleSwitch from "./ui/ToggleSwitch";
import ErrorModal from "../Modal/ErrorModal";

const PrivacySection = () => {
  const { user, apiCall, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Interaction settings
  const [allowComments, setAllowComments] = useState(1);
  const [allowLikes, setAllowLikes] = useState(1);

  // Profile visibility settings
  const [showFollowing, setShowFollowing] = useState(1);
  const [showFollowers, setShowFollowers] = useState(1);
  const [showAdmirers, setShowAdmirers] = useState(0);
  const [showContactButton, setShowContactButton] = useState(0);

  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const [readingMode, setReadingModeEnabled] = useReadingMode();

  // Load privacy settings from user profile
  useEffect(() => {
    if (user) {
      if (user.profile?.privacySettings) {
        const settings = user.profile.privacySettings;

        // Set interaction settings
        setAllowComments(settings.allowComments);
        setAllowLikes(settings.allowLikes);

        // Set profile visibility settings
        setShowFollowing(settings.showFollowing);
        setShowFollowers(settings.showFollowers);
        setShowAdmirers(settings.showAdmirers);
        setShowContactButton(settings.showContactButton);
      }

      setIsLoading(false);
    }
  }, [user]);

  // Function to save privacy settings
  const savePrivacySetting = async (settingKey, value) => {
    setIsSaving(true);

    try {
      const result = await updatePrivacySettings(apiCall, settingKey, value);

      if (result.success) {
        if (refreshUser) {
          await refreshUser();
        }
        return { success: true };
      }

      return {
        success: false,
        error: result.error || "Failed to update this setting.",
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to update this setting.",
      };
    } finally {
      setIsSaving(false);
    }
  };

  // Handler functions that save changes
  const handleAllowCommentsChange = async (value) => {
    const newValue = value ? 1 : 0;
    const previousValue = allowComments;

    setAllowComments(newValue);
    const result = await savePrivacySetting("allowComments", newValue);

    if (!result.success) {
      setAllowComments(previousValue);
      setErrorModal({ isOpen: true, message: result.error });
    }
  };

  const handleAllowLikesChange = async (value) => {
    const newValue = value ? 1 : 0;
    const previousValue = allowLikes;

    setAllowLikes(newValue);
    const result = await savePrivacySetting("allowLikes", newValue);

    if (!result.success) {
      setAllowLikes(previousValue);
      setErrorModal({ isOpen: true, message: result.error });
    }
  };

  const handleShowFollowingChange = async (value) => {
    const newValue = value ? 1 : 0;
    const previousValue = showFollowing;

    setShowFollowing(newValue);
    const result = await savePrivacySetting("showFollowing", newValue);

    if (!result.success) {
      setShowFollowing(previousValue);
      setErrorModal({ isOpen: true, message: result.error });
    }
  };

  const handleShowFollowersChange = async (value) => {
    const newValue = value ? 1 : 0;
    const previousValue = showFollowers;

    setShowFollowers(newValue);
    const result = await savePrivacySetting("showFollowers", newValue);

    if (!result.success) {
      setShowFollowers(previousValue);
      setErrorModal({ isOpen: true, message: result.error });
    }
  };

  const handleShowAdmirersChange = async (value) => {
    const newValue = value ? 1 : 0;
    const previousValue = showAdmirers;

    setShowAdmirers(newValue);
    const result = await savePrivacySetting("showAdmirers", newValue);

    if (!result.success) {
      setShowAdmirers(previousValue);
      setErrorModal({ isOpen: true, message: result.error });
    }
  };

  const handleShowContactButtonChange = async (value) => {
    const newValue = value ? 1 : 0;
    const previousValue = showContactButton;

    setShowContactButton(newValue);
    const result = await savePrivacySetting("showContactButton", newValue);

    if (!result.success) {
      setShowContactButton(previousValue);
      setErrorModal({ isOpen: true, message: result.error });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Interactions</h3>

        <div className="space-y-3">
          {/* Interaction: Comments */}
          <div className="flex items-center">
            <div className="flex items-center space-x-3 w-4/5">
              <MessageCircle className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <span className="text-gray-900">Allow Comments</span>
            </div>
            <div className="w-1/5 flex justify-end">
              <ToggleSwitch
                checked={allowComments === 1}
                onChange={handleAllowCommentsChange}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Interaction: Likes */}
          <div className="flex items-center">
            <div className="flex items-center space-x-3 w-4/5">
              <Heart className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <span className="text-gray-900">Allow Likes</span>
            </div>
            <div className="w-1/5 flex justify-end">
              <ToggleSwitch
                checked={allowLikes === 1}
                onChange={handleAllowLikesChange}
                disabled={isSaving}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Profile Visibility</h3>

        <div className="space-y-3">
          {/* Visibility: Following */}
          <div className="flex items-center">
            <div className="flex items-center space-x-3 w-4/5">
              <Users className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <div>
                <p className="text-gray-900">Show Following Count</p>
                <p className="text-xs text-gray-500">
                  People can see how many people you follow
                </p>
              </div>
            </div>
            <div className="w-1/5 flex justify-end">
              <ToggleSwitch
                checked={showFollowing === 1}
                onChange={handleShowFollowingChange}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Visibility: Followers */}
          <div className="flex items-center">
            <div className="flex items-center space-x-3 w-4/5">
              <Users className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <div>
                <p className="text-gray-900">Show Followers Count</p>
                <p className="text-xs text-gray-500">
                  People can see how many followers you have
                </p>
              </div>
            </div>
            <div className="w-1/5 flex justify-end">
              <ToggleSwitch
                checked={showFollowers === 1}
                onChange={handleShowFollowersChange}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Visibility: Admirers */}
          <div className="flex items-center">
            <div className="flex items-center space-x-3 w-4/5">
              <Users className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <div>
                <p className="text-gray-900">Show Admirers Count</p>
                <p className="text-xs text-gray-500">
                  People can see how many admirers you have
                </p>
              </div>
            </div>
            <div className="w-1/5 flex justify-end">
              <ToggleSwitch
                checked={showAdmirers === 1}
                onChange={handleShowAdmirersChange}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Visibility: Contact Button */}
          <div className="pt-3 border-t">
            <div className="flex items-center">
              <div className="flex items-center space-x-3 w-4/5">
                <Mail className="w-5 h-5 text-gray-600 flex-shrink-0" />
                <div>
                  <p className="text-gray-900">Show Contact Button</p>
                  <p className="text-xs text-gray-500">
                    Display a contact button on your profile
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    When enabled, people will be able to see your email address.
                  </p>
                </div>
              </div>
              <div className="w-1/5 flex justify-end">
                <ToggleSwitch
                  checked={showContactButton === 1}
                  onChange={handleShowContactButtonChange}
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Reading Experience</h3>

        <div className="flex items-center">
          <div className="flex items-center space-x-3 w-4/5">
            <div>
              <p className="text-gray-900">Reading Mode</p>
              <p className="text-xs text-gray-500">
                Hide likes, comments, collections, and post options while
                browsing your feed
              </p>
            </div>
          </div>
          <div className="w-1/5 flex justify-end">
            <ToggleSwitch
              checked={readingMode}
              onChange={setReadingModeEnabled}
            />
          </div>
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </div>
  );
};

export default PrivacySection;
